<script setup lang="ts">
import { ElTransfer } from 'element-plus'

import type { TransferItem } from './types'

/**
 * Two panels with a search over each, for moving items from an available list into a chosen
 * one — `v-model` is the keys on the right.
 *
 * Worth the room only when the reader has to see what they have *not* picked as well as
 * what they have. A field that merely takes several values is a `Select` with `multiple`,
 * and a fixed set of a few is a `CheckboxGroup`.
 *
 * The whole list is rendered, so it is not for a catalogue that has to be searched on
 * the server.
 */
withDefaults(
  defineProps<{
    modelValue?: Array<string | number>
    data: TransferItem[]
    titles?: [string, string]
    filterable?: boolean
    filterPlaceholder?: string
    /** Keys checked in the left panel on mount */
    leftDefaultChecked?: Array<string | number>
    disabled?: boolean
  }>(),
  {
    filterable: true,
    titles: () => ['Available', 'Selected']
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: Array<string | number>): void
}>()
</script>

<template>
  <ElTransfer
    class="shared-transfer"
    :model-value="modelValue"
    :data="data"
    :titles="titles"
    :filterable="filterable"
    :filter-placeholder="filterPlaceholder"
    :left-default-checked="leftDefaultChecked"
    :disabled="disabled"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>

<style lang="scss" scoped>
.shared-transfer {
  display: flex;
  width: 100%;
}

.shared-transfer :deep(.el-transfer__buttons) {
  flex: 0 0 auto;
  padding: 0 10px;
}

/* Element Plus sizes panels at a fixed 200px; let both fill the row instead. */
.shared-transfer :deep(.el-transfer-panel) {
  flex: 1 1 0;
  min-width: 200px;
}

.shared-transfer :deep(.el-transfer-panel__item) {
  display: block;
}
</style>
